// PDF.js for the flipbook: loaded on first use (it is a megabyte the product
// page does not need until somebody opens a book), in its legacy build — the
// one that brings its own polyfills, so an older Safari opens a book too.
//
// A book is opened by range requests: the first pages show while the rest of
// the file is still on the server, and only the pages turned to are fetched.
// The media host answers `Accept-Ranges` and `Access-Control-Allow-Origin`.
import pdfjsPackage from "pdfjs-dist/package.json"

// the files PDF.js reads by url (scripts/copy-pdfjs-assets.mjs puts them here)
const ASSETS = `/static/pdfjs/${pdfjsPackage.version}/`

let loading = null

/** The PDF.js module, its worker set up. */
export function loadPdfjs() {
  if (loading) return loading
  loading = Promise.all([
    import("pdfjs-dist/legacy/build/pdf.min.mjs"),
    import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"),
  ])
    .then(([pdfjs, worker]) => {
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default
      return pdfjs
    })
    .catch((error) => {
      loading = null
      throw error
    })
  return loading
}

/** Open the PDF at `url`: a PDF.js loading task (`.promise`, `.destroy()`). */
export async function openPdf(url) {
  const pdfjs = await loadPdfjs()
  return pdfjs.getDocument({
    url,
    cMapUrl: `${ASSETS}cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `${ASSETS}standard_fonts/`,
    wasmUrl: `${ASSETS}wasm/`,
    iccUrl: `${ASSETS}iccs/`,
    // pages as they are needed, not the whole file up front
    disableAutoFetch: true,
    disableStream: true,
    rangeChunkSize: 256 * 1024,
  })
}

/**
 * One page drawn as an image `height` pixels tall: an object url of a JPEG,
 * which the browser keeps compressed — a canvas per page would hold every
 * page of a long book uncompressed in memory. The caller revokes the url.
 */
export async function renderPageImage(pdf, pageNumber, height) {
  const page = await pdf.getPage(pageNumber)
  const base = page.getViewport({ scale: 1 })
  const viewport = page.getViewport({ scale: height / base.height })
  const canvas = document.createElement("canvas")
  canvas.width = Math.ceil(viewport.width)
  canvas.height = Math.ceil(viewport.height)
  const context = canvas.getContext("2d", { alpha: false })
  context.fillStyle = "#fff"
  context.fillRect(0, 0, canvas.width, canvas.height)
  try {
    await page.render({ canvasContext: context, canvas, viewport }).promise
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88))
    return blob ? URL.createObjectURL(blob) : canvas.toDataURL("image/jpeg", 0.88)
  } finally {
    page.cleanup()
    canvas.width = 0
    canvas.height = 0
  }
}
